// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

import { useEffect, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { AlertCircle, CheckCircle2, Loader2 } from 'lucide-react'
import PasswordField from '../components/PasswordField'
import { completePasswordReset } from '../api/client'

interface Props { onScreenEnter: () => void }

export default function ResetPasswordPage({ onScreenEnter }: Props) {
  useEffect(() => { onScreenEnter() }, [onScreenEnter])

  const [params] = useSearchParams()
  const token = params.get('token') || ''
  const navigate = useNavigate()

  const [pw, setPw] = useState('')
  const [pw2, setPw2] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [done, setDone] = useState(false)
  const [error, setError] = useState<string | null>(null)

  if (!token) {
    return (
      <div style={containerStyle}>
        <div style={errorBigStyle}>
          <AlertCircle size={20} aria-hidden="true" />
          <div>
            <strong>This reset link is invalid.</strong>
            <p style={{ margin: '6px 0 0', fontSize: 13, lineHeight: 1.5 }}>
              The link is missing its token. Please request a new one.
            </p>
          </div>
        </div>
        <div style={altLinksStyle}>
          <Link to="/forgot-password" style={linkStyle}>Request a new link</Link>
        </div>
      </div>
    )
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (submitting) return
    setError(null)
    if (pw.length < 12) { setError('Password must be at least 12 characters.'); return }
    if (pw !== pw2)     { setError('Passwords don’t match.'); return }

    setSubmitting(true)
    try {
      const res = await completePasswordReset(token, pw)
      setDone(true)
      // Send the user to THEIR pharmacy's branded login page so they know where to sign in. The branded
      // login route is /p/{url_code}/login — url_code is the opaque code real pharmacy URLs use; prefer
      // it, fall back to slug, then to the generic /login for platform accounts with no pharmacy.
      const code = res?.pharmacy_url_code || res?.pharmacy_slug
      const dest = code ? `/p/${code}/login` : '/login'
      setTimeout(() => navigate(dest, { replace: true }), 2000)
    } catch (e: any) {
      const code = e?.body?.error
      const msg =
        code === 'invalid_token'   ? 'This reset link is no longer valid.' :
        code === 'expired'         ? 'This reset link has expired. Request a new one.' :
        code === 'already_used'    ? 'This reset link has already been used.' :
        code === 'weak_password'   ? 'Password must be at least 12 characters.' :
        e?.body?.message || 'Could not reset password. Please try again.'
      setError(msg)
    } finally {
      setSubmitting(false)
    }
  }

  if (done) {
    return (
      <div style={containerStyle}>
        <div style={successStyle}>
          <CheckCircle2 size={20} aria-hidden="true" />
          <div>
            <strong>Password updated.</strong>
            <p style={{ margin: '6px 0 0', fontSize: 13, lineHeight: 1.5 }}>
              Redirecting you to sign in…
            </p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div style={containerStyle}>
      <h1 style={titleStyle}>Set a new password</h1>
      <p style={subtitleStyle}>
        Choose a strong password — at least 12 characters. Avoid passwords
        you use elsewhere.
      </p>

      <form onSubmit={handleSubmit} style={formStyle} noValidate>
        <label style={labelStyle} htmlFor="pw">New password</label>
        <PasswordField
          id="pw"
          autoComplete="new-password"
          value={pw}
          onChange={setPw}
          disabled={submitting}
          minLength={12}
          required
          style={inputStyle}
        />

        <label style={labelStyle} htmlFor="pw2">Confirm new password</label>
        <PasswordField
          id="pw2"
          autoComplete="new-password"
          value={pw2}
          onChange={setPw2}
          disabled={submitting}
          minLength={12}
          required
          style={inputStyle}
        />

        {error && (
          <div role="alert" style={errorStyle}>
            <AlertCircle size={16} aria-hidden="true" />
            <span>{error}</span>
          </div>
        )}

        <button type="submit" disabled={submitting} style={submitStyle}>
          {submitting ? (<><Loader2 size={16} className="spin" /> Updating…</>) : 'Set new password'}
        </button>
      </form>
    </div>
  )
}

const containerStyle: React.CSSProperties = { maxWidth: 440, margin: '0 auto', padding: '32px 24px' }
const titleStyle: React.CSSProperties = { fontFamily: 'Georgia, serif', fontSize: 28, fontWeight: 700, margin: '0 0 8px', letterSpacing: -0.5 }
const subtitleStyle: React.CSSProperties = { margin: '0 0 24px', color: 'var(--color-text-soft)', fontSize: 14, lineHeight: 1.5 }
const formStyle: React.CSSProperties = { display: 'flex', flexDirection: 'column', gap: 6 }
const labelStyle: React.CSSProperties = { fontSize: 13, fontWeight: 500, marginTop: 12 }
const inputStyle: React.CSSProperties = { padding: '10px 12px', border: '1px solid var(--color-border)', borderRadius: 6, fontSize: 14, fontFamily: 'inherit', background: 'var(--color-surface)' }
const errorStyle: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 8, marginTop: 12, padding: '8px 12px', background: '#fee2e2', border: '1px solid #fecaca', borderRadius: 4, color: '#991b1b', fontSize: 13 }
const errorBigStyle: React.CSSProperties = { display: 'flex', gap: 12, padding: '14px 16px', background: '#fee2e2', border: '1px solid #fecaca', borderRadius: 6, color: '#991b1b', fontSize: 14 }
const successStyle: React.CSSProperties = { display: 'flex', gap: 12, padding: '14px 16px', background: '#ecfdf5', border: '1px solid #a7f3d0', borderRadius: 6, color: '#065f46', fontSize: 14 }
const submitStyle: React.CSSProperties = { marginTop: 18, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: '12px 18px', background: 'var(--color-primary, #047857)', color: '#fff', border: 'none', borderRadius: 6, fontSize: 14, fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit' }
const altLinksStyle: React.CSSProperties = { display: 'flex', gap: 12, alignItems: 'center', justifyContent: 'center', marginTop: 24, fontSize: 13 }
const linkStyle: React.CSSProperties = { color: 'var(--color-primary, #047857)', textDecoration: 'none' }
